import {
    firstNameValidator,
    fundraisingGroupValidator,
    lastNameValidator,
    studentNameValidator,
    studentNameValidatorRequired,
} from 'common/modules/form-validation/validators';
import { ActivityIndicator } from 'components/activity-indicator/ActivityIndicator';
import { typedFormikField } from 'components/fields/utils/typedFormikField';
import { FormikTextField, FormikCheckboxField } from 'components/fields/FormikTextField';
import { Form, Formik } from 'formik';
import React, { useCallback, useContext, useMemo } from 'react';
import { FamilyEnrollmentContext } from '../../../features/family-enrollment/context';
import { BaseStep } from './BaseStep';
import { ButtonControls } from './utils';
import { FieldSpacer } from 'components/fields/FieldSpacer';
import { OrganizationType } from 'common/api/store/models/OrganizationType';
import { GA_WEB_ENROLLMENT_EXPERIMENT_NAME, logParticipantEnrollment } from 'config/analytics';
import { IS_FLIPGIVE } from 'common/env';
import { createStyles } from 'utils/createStyle';

const marketingOptInStyles = createStyles({
    wrapper: [
        '',
        {
            '& div.flex.flex-row': { alignItems: 'flex-start' },
        },
    ],
});

const MARKETING_OPT_IN_COPY =
    "Yes, I'd like to receive emails from FlipGive so I don't miss out on bonus earnings, online shopping deals, and new brand updates. I understand I can unsubscribe at any time.";
const FLIPGIVE_SUPPORT_PHONE = '800-727-4715';
const FLIPGIVE_SUPPORT_PHONE_OPTION = 'Option 3';
const FLIPGIVE_SUPPORT_EMAIL = 'support@flipgive.com';

interface FamilyAccountRegistration {
    firstName: string;
    lastName: string;
    studentName: string;
    fundraisingGroup: string;
    marketingOptIn: boolean;
}

const RegistrationRequestField = typedFormikField<FamilyAccountRegistration>()(FormikTextField);

export const CreateFamilyAccount = () => {
    const {
        stepHeaders,
        organization,
        userAccountInfo,
        updateUserAccountInfo,
        goToNextStep,
        isEnrollmentTestVariant,
        isSocialRegistrationEnabled,
    } = useContext(FamilyEnrollmentContext);

    const isCanadianUser = organization?.countryCode === 'CA' || IS_FLIPGIVE;
    const showMarketingOptIn = isSocialRegistrationEnabled && isCanadianUser;

    const initialValues: FamilyAccountRegistration = {
        firstName: userAccountInfo?.firstName || '',
        lastName: userAccountInfo?.lastName || '',
        studentName: userAccountInfo?.studentName || '',
        fundraisingGroup: userAccountInfo?.fundraisingGroup || '',
        marketingOptIn: showMarketingOptIn ? userAccountInfo?.marketingOptIn ?? false : false,
    };

    const _onSubmit = useCallback(
        async ({
            firstName,
            lastName,
            studentName,
            fundraisingGroup,
            marketingOptIn,
        }: FamilyAccountRegistration) => {
            updateUserAccountInfo((_) => ({
                ..._,
                firstName,
                lastName,
                studentName,
                fundraisingGroup,
                ...(showMarketingOptIn && { marketingOptIn }),
            }));

            logParticipantEnrollment(
                isEnrollmentTestVariant ? 3 : 2,
                'create_an_account',
                { flowVariant: isEnrollmentTestVariant ? 'variant' : 'control' },
                {
                    experiment_name: GA_WEB_ENROLLMENT_EXPERIMENT_NAME,
                    experiment_variant: isEnrollmentTestVariant ? 'variant' : 'control',
                }
            );
            goToNextStep();
        },
        [updateUserAccountInfo, goToNextStep, isEnrollmentTestVariant]
    );

    const enrollCode = useMemo(() => localStorage.getItem('RR_enrollmentCode') || '', []);
    const enrollCodeFromP2P =
        enrollCode.startsWith('RR') &&
        (OrganizationType.ParochialSchool === organization?.type ||
            OrganizationType.BandMusic === organization?.type ||
            OrganizationType.AthleticGroup === organization?.type);

    if (!stepHeaders) {
        return <ActivityIndicator />;
    }

    return (
        <BaseStep
            contentClassName="text-md sm:text-lg font-thin pb-4"
            heading={
                isSocialRegistrationEnabled
                    ? 'Account Details'
                    : (stepHeaders.createAccountHeading as string)
            }
            subheading={isSocialRegistrationEnabled ? null : stepHeaders.createAccountBody}
            subheadingData={{ OrganizationName: organization?.name }}
        >
            <Formik initialValues={initialValues} onSubmit={_onSubmit}>
                {(formikProps) => (
                    <Form>
                        <div>
                            <section className="grid grid-cols-1 md:grid-cols-1 gap-x-6 gap-y-6 my-4 pt-2">
                                <RegistrationRequestField
                                    name="firstName"
                                    label="First Name"
                                    validate={firstNameValidator}
                                />
                                <RegistrationRequestField
                                    name="lastName"
                                    label="Last Name"
                                    validate={lastNameValidator}
                                />
                                <div className="mt-4">
                                    <div className="mb-6 text-brand-dark text-lg">
                                        Who are you earning for?
                                    </div>
                                    <RegistrationRequestField
                                        name="studentName"
                                        label={
                                            <label className="font-bold">
                                                Student's Name{' '}
                                                {!enrollCodeFromP2P ? (
                                                    <span className="text-grey-medium font-normal">
                                                        (optional)
                                                    </span>
                                                ) : null}
                                            </label>
                                        }
                                        validate={
                                            !enrollCodeFromP2P
                                                ? studentNameValidator
                                                : studentNameValidatorRequired
                                        }
                                    />
                                    {!formikProps.errors.studentName ||
                                    !formikProps.touched.studentName ? (
                                        <span className="mt-2 text-grey-medium text-xs">
                                            Enter the name of the person you are earning for; this
                                            could be an athlete, musician, etc.
                                        </span>
                                    ) : null}
                                </div>
                                <div>
                                    <RegistrationRequestField
                                        name="fundraisingGroup"
                                        label={
                                            <label className="font-bold">
                                                Group{' '}
                                                <span className="text-grey-medium font-normal">
                                                    (optional)
                                                </span>
                                            </label>
                                        }
                                        validate={fundraisingGroupValidator}
                                    />
                                    <span className="mt-2 text-grey-medium text-xs">
                                        Team name, classroom, etc.
                                    </span>
                                </div>
                                {showMarketingOptIn && (
                                    <>
                                        <FieldSpacer />
                                        <div className={marketingOptInStyles.wrapper}>
                                            <FormikCheckboxField
                                                name="marketingOptIn"
                                                id="marketingOptIn"
                                                label={MARKETING_OPT_IN_COPY}
                                                className="text-grey-1 text-sm font-semibold pl-1 leading-4"
                                                checked={formikProps.values.marketingOptIn}
                                            />
                                        </div>
                                        {IS_FLIPGIVE && (
                                            <div className="text-xs text-grey-2">
                                                <a
                                                    href={`tel:${FLIPGIVE_SUPPORT_PHONE}`}
                                                    className="text-brand underline"
                                                >
                                                    {FLIPGIVE_SUPPORT_PHONE}
                                                </a>{' '}
                                                ({FLIPGIVE_SUPPORT_PHONE_OPTION}) |{' '}
                                                <a
                                                    href={`mailto:${FLIPGIVE_SUPPORT_EMAIL}`}
                                                    className="text-brand underline"
                                                >
                                                    {FLIPGIVE_SUPPORT_EMAIL}
                                                </a>
                                            </div>
                                        )}
                                    </>
                                )}
                            </section>
                        </div>
                        <ButtonControls />
                    </Form>
                )}
            </Formik>
        </BaseStep>
    );
};
